import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { addDays, parseISO } from "date-fns";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  Coffee,
  Layers,
  Sparkles,
} from "lucide-react";
import { PlanningQualityNotice } from "@/components/plan/PlanningQualityNotice";
import { GoalAssessmentCard } from "@/components/plan/GoalAssessmentCard";
import { PrepareDetailsButton, PreparationReason } from "@/components/plan/PrepareDetailsButton";
import { BackButton } from "@/components/ui/BackButton";
import { getPlanOverview, getWeek } from "@/api/plan";
import { getProfile } from "@/api/profile";
import { triggerWeeklyReview } from "@/api/coach";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { fmtDate, fmtWeekRange } from "@/lib/dates";
import { plannedWorkoutTitle, workoutTypeLabel } from "@/lib/planned-workout-labels";
import { LoadError } from "@/components/ui/LoadError";
import { useAuthStore } from "@/stores/auth";
import { fmtDuration, SPORT_LABELS } from "@/lib/tss";
import type { PlannedWorkout, SessionBlock } from "@/types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function _weekdayLabel(idx: number): string {
  // Mon=0..Sun=6, anchor on a known Monday (Jan 5 1970)
  const d = new Date(1970, 0, 5 + idx);
  return fmtDate(d, "EEEEEE");
}

function isPreview(workout: PlannedWorkout): boolean {
  return workout.status === "skeleton" || (!!workout.detail_status && workout.detail_status !== "ready");
}

function ownerToday(timezone: string | undefined): string | null {
  if (!timezone) return null;
  try {
    const parts = new Intl.DateTimeFormat("en", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
    return ["year", "month", "day"].map(part => parts.find(value => value.type === part)?.value).join("-");
  } catch { return null; }
}

const KEY_SESSION_TYPES = new Set([
  "sub_threshold",
  "threshold",
  "sweet_spot",
  "brick",
  "brick_race_pace",
  "race_pace",
  "race_simulation",
  "race_rehearsal",
  "long",
  "vo2",
  "long_z2",
]);

function isKeySession(w: PlannedWorkout): boolean {
  if ((w.session_blocks || []).length > 0) return true;
  if (KEY_SESSION_TYPES.has(w.type)) return true;
  if (w.duration_minutes >= 90) return true;
  return false;
}

function workoutsByDayOffset(
  workouts: PlannedWorkout[],
  weekStartISO: string,
): Map<number, PlannedWorkout[]> {
  const start = parseISO(weekStartISO);
  const startMs = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  ).getTime();
  const m = new Map<number, PlannedWorkout[]>();
  for (const w of workouts) {
    const d = parseISO(w.date);
    const dMs = new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
    ).getTime();
    const offset = Math.round((dMs - startMs) / 86400000);
    if (offset < 0 || offset > 6) continue;
    const arr = m.get(offset);
    if (arr) arr.push(w);
    else m.set(offset, [w]);
  }
  return m;
}

// ---------------------------------------------------------------------------
// Day cards
// ---------------------------------------------------------------------------

function DayHeader({ date, offset }: { date: Date; offset: number }) {
  return (
    <div className="flex w-16 shrink-0 flex-col items-center">
      <span className="text-[10px] uppercase tracking-wider text-taupe">
        {_weekdayLabel(offset)}
      </span>
      <span className="metric-number text-[18px]">{date.getDate()}</span>
      <span className="text-[10px] text-taupe">
        {fmtDate(date, "MMM")}
      </span>
    </div>
  );
}

function RestDayCard({ date, offset }: { date: Date; offset: number }) {
  const { t } = useTranslation();
  return (
    <Card className="flex items-center gap-4 bg-cream/60">
      <DayHeader date={date} offset={offset} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Coffee className="h-3.5 w-3.5 text-amber-dark" strokeWidth={1.8} />
          <span className="font-serif text-[16px] text-navy-deep">
            {t("week_detail.rest_day")}
          </span>
        </div>
      </div>
    </Card>
  );
}

function SimpleWorkoutCard({
  workout,
  date,
  offset,
  week,
}: {
  workout: PlannedWorkout;
  date: Date;
  offset: number;
  week: number;
}) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/workout/planned/${workout.id}`}
      state={{ week }}
    >
      <Card
        className={
          isKeySession(workout)
            ? "flex items-center gap-4 border-amber/40 ring-1 ring-amber/20 hover:border-amber/60"
            : "flex items-center gap-4 hover:border-amber/40"
        }
      >
        <DayHeader date={date} offset={offset} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-amber/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-dark">
              {SPORT_LABELS[workout.sport] ?? workout.sport}
            </span>
            <span className="text-[12px] text-taupe">{workoutTypeLabel(workout.type)}</span>
            {isPreview(workout) && <span className="text-[10px] uppercase text-amber-dark">{t("workout_detail.preliminary")}</span>}
            {isKeySession(workout) ? (
              <span className="rounded-full bg-navy px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider text-amber-light">
                {t("week_detail.key")}
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 font-medium leading-tight">{plannedWorkoutTitle(workout)}</div>
          <div className="mt-0.5 text-[12px] text-taupe">
            {fmtDuration(workout.duration_minutes)} · TSS {workout.target_tss}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-taupe-light" strokeWidth={1.6} />
      </Card>
    </Link>
  );
}

function CompoundWorkoutCard({
  workout,
  date,
  offset,
  week,
}: {
  workout: PlannedWorkout;
  date: Date;
  offset: number;
  week: number;
}) {
  const { t } = useTranslation();
  const blocks = workout.session_blocks;
  return (
    <Link
      to={`/workout/planned/${workout.id}`}
      state={{ week }}
    >
      <Card className="border-amber/40 ring-1 ring-amber/20 hover:border-amber/60">
        <div className="flex items-start gap-4">
          <DayHeader date={date} offset={offset} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-amber/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-dark">
                <Layers className="h-3 w-3" strokeWidth={1.8} /> {t("week_detail.compound")}
              </span>
              <span className="text-[12px] text-taupe">
                {blocks.map((b) => SPORT_LABELS[b.sport] ?? b.sport).join(" + ")}
              </span>
            </div>
            <div className="mt-1 font-medium leading-tight">{workout.title}</div>

            <div className="mt-3 space-y-2 border-l-2 border-amber/30 pl-3">
              {blocks.map((b, i) => (
                <SessionBlockLine key={i} block={b} />
              ))}
            </div>

            <div className="mt-3 border-t border-cream-border pt-2 text-[12px] text-taupe">
              {fmtDuration(workout.duration_minutes)} · TSS {workout.target_tss}{" "}
              {t("week_detail.total")}
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-taupe-light" strokeWidth={1.6} />
        </div>
      </Card>
    </Link>
  );
}

function SessionBlockLine({ block }: { block: SessionBlock }) {
  const { t } = useTranslation();
  const label =
    block.block === "endurance"
      ? `${SPORT_LABELS[block.sport] ?? block.sport} · ${block.target || "—"}`
      : block.block === "strength"
        ? `${t("week_detail.strength")} · ${block.focus || "—"}${block.back_safe ? ` · ${t("week_detail.back_adapted")}` : ""}`
        : `${block.block} · ${block.target || ""}`;
  return (
    <div>
      <div className="text-[13px] font-medium text-navy-deep">{label}</div>
      <div className="text-[11px] text-taupe">
        {fmtDuration(block.duration_min)}
        {block.tss != null ? ` · TSS ${block.tss}` : ""}
      </div>
      {block.exercises && block.exercises.length > 0 ? (
        <ul className="mt-1 space-y-0.5 text-[11px] text-taupe">
          {block.exercises.map((ex, i) => (
            <li key={i}>· {ex}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function WeekDetail() {
  const { t } = useTranslation();
  const { weekNumber } = useParams<{ weekNumber: string }>();
  const n = Number(weekNumber);
  const qc = useQueryClient();
  const [regenMsg, setRegenMsg] = useState<string | null>(null);
  const token = useAuthStore(state => state.token);
  const mounted = useRef(false);
  const activeView = useRef({ token, n });
  activeView.current = { token, n };
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setRegenMsg(null); }, [n, token]);

  const weekQ = useQuery({
    queryKey: ["week", n],
    queryFn: ({ signal }) => getWeek(n, signal),
    enabled: !Number.isNaN(n),
  });
  const planQ = useQuery({ queryKey: ["plan"], queryFn: getPlanOverview });
  const profileQ = useQuery({ queryKey: ["profile"], queryFn: getProfile });
  const { data: week, isLoading } = weekQ;
  const refresh = () => { void weekQ.refetch(); void planQ.refetch(); void profileQ.refetch(); };
  const currentMutation = (variables: { token: string | null; n: number }) => mounted.current && useAuthStore.getState().token === variables.token && activeView.current.n === variables.n;

  const regen = useMutation({
    mutationFn: (variables: { start: string; token: string | null; n: number }) => triggerWeeklyReview(variables.start, variables.token),
    onSuccess: (r, variables) => {
      if (!currentMutation(variables)) return;
      if (r.status === "skipped") {
        const message = r.reason === "strava_source_restricted" ? "workout_detail.metrics_unavailable_note"
          : r.reason === "health_review_required" ? "planning_quality.health_body" : "common.generation_skipped";
        setRegenMsg(t(message));
        refresh();
        return;
      }
      setRegenMsg(r.status === "cached"
        ? t("common.generation_cached")
        : t("week_detail.regen_success", { n: r.workouts_created, model: r.model_used }));
      qc.invalidateQueries({ queryKey: ["week", n] });
      qc.invalidateQueries({ queryKey: ["plan"] });
      qc.invalidateQueries({ queryKey: ["plan-workouts"] });
      qc.invalidateQueries({ queryKey: ["planned-workout"] });
    },
    onError: (error, variables) => {
      if (!currentMutation(variables)) return;
      const response = (error as { response?: { status?: number; data?: { detail?: string } } }).response;
      const changed = response?.status === 409 && response.data?.detail === "weekly_target_changed";
      setRegenMsg(t(changed ? "week_detail.target_changed" : "week_detail.regen_fail"));
      if (changed) refresh();
    },
  });

  if (isLoading) {
    return <div className="py-10 text-center text-taupe">{t("week_detail.loading")}</div>;
  }
  const errorStatus = (weekQ.error as { response?: { status?: number } } | null)?.response?.status;
  if (weekQ.isError && errorStatus !== 404 && (!week || errorStatus === 401 || errorStatus === 403)) {
    return <LoadError onRetry={refresh} retrying={weekQ.isFetching} />;
  }
  if (!week || errorStatus === 404) {
    return <div className="py-10 text-center text-coral">{t("week_detail.week_not_found")}</div>;
  }

  const byOffset = workoutsByDayOffset(week.workouts, week.week_start_date);
  const trainingDaysCount = [...byOffset.keys()].length;
  const restDaysCount = 7 - trainingDaysCount;
  const keySessionsCount = week.workouts.filter(isKeySession).length;
  const previews = week.workouts.filter(isPreview);
  const sourceBlocked = previews.some(workout => workout.detail_status === "blocked_source");
  const healthBlocked = !!week.current_health_policy?.block_automatic_training || previews.some(workout => workout.detail_status === "blocked_health");
  const today = ownerToday(profileQ.data?.timezone);
  const targetWeek = today ? planQ.data?.weeks.filter(candidate => candidate.week_start_date >= today).sort((a, b) => a.week_start_date.localeCompare(b.week_start_date))[0] : undefined;
  const canGenerate = !weekQ.isError && !planQ.isError && !profileQ.isError && !healthBlocked && !sourceBlocked
    && targetWeek?.week_number === week.week_number && targetWeek?.week_start_date === week.week_start_date;
  const refreshing = weekQ.isFetching || planQ.isFetching || profileQ.isFetching;

  return (
    <div className="space-y-5">
      <BackButton to="/plan" label={t("week_detail.back_to_plan")} />

      <div>
        <SectionLabel>
          {t("week_detail.week_phase", { n: week.week_number, phase: week.phase })}
        </SectionLabel>
        <h1 className="mt-1 font-serif text-[28px] font-medium tracking-tight">
          {fmtWeekRange(week.week_start_date)}
        </h1>
      </div>

      {!planQ.isError && <GoalAssessmentCard assessment={planQ.data?.goal_assessment} />}

      <PlanningQualityNotice provisional={week.provisional} notes={week.planning_notes}
        healthPolicy={week.current_health_policy} />

      {previews.length > 0 && <Card className="border-amber/40">
        <SectionLabel>{t("workout_detail.preliminary")}</SectionLabel>
        <p className="mt-2 text-sm">{t("week_detail.preview_desc")}</p>
        {week.can_prepare_details && !weekQ.isError && !sourceBlocked && !healthBlocked &&
          <div className="mt-3"><PrepareDetailsButton weekNumber={week.week_number} weekStart={week.week_start_date} disabled={refreshing || regen.isPending} /></div>}
        {!week.can_prepare_details && !sourceBlocked && !healthBlocked && <PreparationReason reason={week.preparation_reason} />}
        {sourceBlocked && <><p className="mt-2 text-sm">{t("workout_detail.preview_source_desc")}</p><Link to="/settings" className="mt-2 inline-block text-sm underline">{t("nav.settings")}</Link></>}
        {healthBlocked && !week.current_health_policy?.block_automatic_training && <><p className="mt-2 text-sm">{t("planning_quality.health_body")}</p><Link to="/settings" className="mt-2 inline-block text-sm underline">{t("nav.settings")}</Link></>}
      </Card>}

      {/* Week summary metric strip */}
      <Card className="px-4 py-3">
        <div className="grid grid-cols-4 gap-3 text-center">
          <SummaryMetric label={t("week_detail.training_days")} value={trainingDaysCount} />
          <SummaryMetric label={t("week_detail.rest_days")} value={restDaysCount} />
          <SummaryMetric label={t("week_detail.key_sessions")} value={keySessionsCount} />
          <SummaryMetric label={t("week_detail.target_tss")} value={week.target_tss} />
        </div>
      </Card>

      {week.claude_review ? (
        <Card tone="navy">
          <SectionLabel className="text-amber-light">
            {t("week_detail.coach_review")}
          </SectionLabel>
          <p className="coach-voice mt-3 whitespace-pre-wrap">
            {week.claude_review}
          </p>
          {week.adjustments_rationale ? (
            <p className="mt-3 border-t border-navy-soft/50 pt-3 text-[13px] italic text-cream-border/80">
              {week.adjustments_rationale}
            </p>
          ) : null}
        </Card>
      ) : (
        <Card tone="navy">
          <SectionLabel className="text-amber-light">
            {t("week_detail.plan_not_built")}
          </SectionLabel>
          <p className="coach-voice mt-3">
            {t("week_detail.plan_not_built_desc")}
          </p>
        </Card>
      )}

      <Button type="button" variant="secondary" className="w-full" disabled={refreshing || regen.isPending} onClick={refresh}>
        {t(refreshing ? "workout_detail.checking_details" : "workout_detail.check_details")}
      </Button>
      {(weekQ.isError || planQ.isError || profileQ.isError) && <p role="alert" className="text-sm text-coral">{t("week_detail.details_refresh_failed")}</p>}
      {canGenerate && !week.can_prepare_details && (!week.preparation_reason || week.preparation_reason === "no_pending_details") && <Button
        variant="secondary"
        size="md"
        className="w-full"
        loading={regen.isPending}
        disabled={refreshing}
        onClick={() => regen.mutate({ start: week.week_start_date, token, n })}
      >
        <Sparkles className="h-4 w-4" />
        {t("week_detail.generate_target")}
      </Button>}
      {regenMsg ? (
        <div className="rounded-lg bg-amber/15 px-3 py-2 text-[13px] text-amber-dark">
          {regenMsg}
        </div>
      ) : null}

      {/* 7-day grid: either workout(s) on that day, or Rest card */}
      {week.workouts.length > 0 ? (
        <div className="space-y-2.5">
          <SectionLabel>{t("week_detail.weekly_workouts")}</SectionLabel>
          {Array.from({ length: 7 }).map((_, offset) => {
            const date = addDays(parseISO(week.week_start_date), offset);
            const dayWorkouts = byOffset.get(offset) || [];
            if (dayWorkouts.length === 0) {
              return (
                <RestDayCard
                  key={`rest-${offset}`}
                  date={date}
                  offset={offset}
                />
              );
            }
            return (
              <div key={`day-${offset}`} className="space-y-2">
                {dayWorkouts.map((w) =>
                  !isPreview(w) && (w.session_blocks || []).length > 0 ? (
                    <CompoundWorkoutCard
                      key={w.id}
                      workout={w}
                      date={date}
                      offset={offset}
                      week={week.week_number}
                    />
                  ) : (
                    <SimpleWorkoutCard
                      key={w.id}
                      workout={w}
                      date={date}
                      offset={offset}
                      week={week.week_number}
                    />
                  ),
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function SummaryMetric({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-taupe">
        {label}
      </div>
      <div className="metric-number mt-0.5 text-[22px]">{value}</div>
    </div>
  );
}
